// PlanSalleEditor.jsx — Phase D : éditeur de plan de salle avec tarifs configurables (multi-tarifs + quotas)
import { useState } from 'react';
import { X, Plus, Trash2, ArrowUp, ArrowDown, Save, Tag, Users } from 'lucide-react';

const COLOR_OPTIONS = [
  { id: 'gold', label: 'Or (VIP)', hex: '#f5a623' },
  { id: 'purple', label: 'Violet (Partenaire)', hex: '#9b59b6' },
  { id: 'blue', label: 'Bleu (Standard)', hex: '#4f8aff' },
  { id: 'green', label: 'Vert (Debout)', hex: '#22d3a0' },
  { id: 'cyan', label: 'Cyan', hex: '#4DFFD2' },
];

// Tarifs proposés comme suggestions rapides
const TARIF_SUGGESTIONS = [
  { id: 'adherent', label: 'Adhérent', prix: 15, default: true },
  { id: 'premiere-visite', label: '1ère visite', prix: 0, description: 'Offerte aux nouveaux' },
  { id: 'invite', label: 'Invité', prix: 18, quota: 10 },
  { id: 'plein-tarif', label: 'Plein tarif', prix: 25 },
];

const DEFAULT_TARIFS = () => [
  { id: 'adherent', label: 'Adhérent', prix: 15, quota: null, default: true, description: '' },
  { id: 'plein-tarif', label: 'Plein tarif', prix: 25, quota: null, default: false, description: '' },
];

const PRESETS = {
  simple: {
    mode: 'simple',
    zones: [
      { id: 'Standard', label: 'Zone Standard', color: 'blue',
        tarifs: DEFAULT_TARIFS(),
        rangees: [
          { row: 'A', seats: [1, 2, 3, 4, 5, '|', 6, 7, 8, 9, 10] },
          { row: 'B', seats: [1, 2, 3, 4, 5, '|', 6, 7, 8, 9, 10] },
          { row: 'C', seats: [1, 2, 3, 4, 5, '|', 6, 7, 8, 9, 10] },
        ] },
    ],
  },
  vip_standard: {
    mode: 'spectacle',
    zones: [
      { id: 'VIP', label: 'Zone VIP', color: 'gold',
        tarifs: [
          { id: 'adherent', label: 'Adhérent', prix: 35, quota: null, default: true, description: '' },
          { id: 'plein-tarif', label: 'Plein tarif', prix: 50, quota: null, default: false, description: '' },
          { id: 'invite', label: 'Invité', prix: 40, quota: 4, default: false, description: 'Invités d\'honneur' },
        ],
        rangees: [
          { row: 'A', seats: [1, 2, 3, 4, '|', 5, 6, 7, 8] },
          { row: 'B', seats: [1, 2, 3, 4, '|', 5, 6, 7, 8] },
        ] },
      { id: 'Standard', label: 'Zone Standard', color: 'blue',
        tarifs: DEFAULT_TARIFS(),
        rangees: [
          { row: 'C', seats: [1, 2, 3, 4, 5, '|', 6, 7, 8, 9, 10] },
          { row: 'D', seats: [1, 2, 3, 4, 5, '|', 6, 7, 8, 9, 10] },
          { row: 'E', seats: [1, 2, 3, 4, 5, '|', 6, 7, 8, 9, 10] },
        ] },
    ],
  },
  fosse: {
    mode: 'simple',
    zones: [
      { id: 'Debout', label: 'Fosse Debout', color: 'green',
        tarifs: [
          { id: 'adherent', label: 'Adhérent', prix: 10, quota: null, default: true, description: '' },
          { id: 'plein-tarif', label: 'Plein tarif', prix: 15, quota: null, default: false, description: '' },
        ],
        standing: true, places: 100 },
    ],
  },
};

// Migre une zone "legacy" (prix_public/prix_membre) vers le nouveau format tarifs[]
function migrateZone(z) {
  if (Array.isArray(z.tarifs) && z.tarifs.length) return z;
  const tarifs = [];
  if (z.prix_membre != null) tarifs.push({ id: 'adherent', label: 'Adhérent', prix: +z.prix_membre || 0, quota: null, default: true, description: '' });
  if (z.prix_public != null) tarifs.push({ id: 'plein-tarif', label: 'Plein tarif', prix: +z.prix_public || 0, quota: null, default: tarifs.length === 0, description: '' });
  if (!tarifs.length) tarifs.push({ id: 'plein-tarif', label: 'Plein tarif', prix: 0, quota: null, default: true, description: '' });
  return { ...z, tarifs };
}

export default function PlanSalleEditor({ initial, onSave, onClose }) {
  const initialZones = initial?.zones?.length
    ? initial.zones.map(migrateZone)
    : PRESETS.vip_standard.zones;
  const [zones, setZones] = useState(initialZones);
  const [mode, setMode] = useState(initial?.mode || 'spectacle');

  const updateZone = (idx, patch) => setZones((z) => z.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const removeZone = (idx) => setZones((z) => z.filter((_, i) => i !== idx));
  const moveZone = (idx, dir) => setZones((z) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= z.length) return z;
    const copy = [...z]; [copy[idx], copy[ni]] = [copy[ni], copy[idx]]; return copy;
  });
  const addZone = (standing = false) => setZones((z) => [...z, standing
    ? { id: `Zone${z.length + 1}`, label: 'Nouvelle zone debout', color: 'green', tarifs: DEFAULT_TARIFS(), standing: true, places: 50 }
    : { id: `Zone${z.length + 1}`, label: 'Nouvelle zone', color: 'blue', tarifs: DEFAULT_TARIFS(), rangees: [{ row: 'A', seats: [1, 2, 3, 4, 5] }] }
  ]);

  const loadPreset = (key) => {
    if (!window.confirm('Remplacer le plan actuel par le modèle choisi ?')) return;
    const preset = JSON.parse(JSON.stringify(PRESETS[key]));
    setZones(preset.zones);
    setMode(preset.mode || 'simple');
  };

  const save = () => {
    const cleaned = zones.map((z) => {
      const tarifs = (z.tarifs || []).map((t) => ({
        id: String(t.id || 'tarif').toLowerCase().replace(/[^a-z0-9_-]/g, '-'),
        label: String(t.label || t.id || 'Tarif').slice(0, 60),
        prix: Math.max(0, +t.prix || 0),
        quota: t.quota != null && t.quota !== '' ? Math.max(0, Math.floor(+t.quota || 0)) : null,
        default: !!t.default,
        description: String(t.description || '').slice(0, 200),
      }));
      // Au moins un tarif par défaut
      if (tarifs.length && !tarifs.some((t) => t.default)) tarifs[0].default = true;
      return {
        ...z,
        tarifs,
        ...(z.standing ? { places: +z.places || 0 } : {}),
      };
    });
    onSave({ mode, zones: cleaned });
  };

  const totalSeats = zones.reduce((acc, z) => acc + (z.standing ? (z.places || 0) : (z.rangees || []).reduce((a, r) => a + r.seats.filter((s) => s !== '|').length, 0)), 0);

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/80 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose} data-testid="plan-editor-modal">
      <div className="card max-w-4xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-lg sm:text-xl font-bold text-slate-900">Éditeur de plan de salle</h3>
            <p className="text-xs text-slate-500 mt-0.5">{zones.length} zone{zones.length > 1 ? 's' : ''} · {totalSeats} place{totalSeats > 1 ? 's' : ''} · Mode <span className="font-bold uppercase">{mode === 'spectacle' ? 'spectacle' : 'simple'}</span></p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>

        {/* Mode + Presets */}
        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Mode :</span>
          <button onClick={() => setMode('simple')} className={`btn-ghost !py-1 !px-2.5 text-xs ${mode === 'simple' ? '!bg-slate-900 !text-white' : ''}`} data-testid="mode-simple">Simple (libre)</button>
          <button onClick={() => setMode('spectacle')} className={`btn-ghost !py-1 !px-2.5 text-xs ${mode === 'spectacle' ? '!bg-slate-900 !text-white' : ''}`} data-testid="mode-spectacle">Mode Spectacle (sièges numérotés)</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5 p-3 bg-slate-50 rounded-lg">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Modèles rapides :</span>
          <button onClick={() => loadPreset('simple')} className="btn-ghost !py-1 !px-2.5 text-xs" data-testid="preset-simple">Salle simple (30)</button>
          <button onClick={() => loadPreset('vip_standard')} className="btn-ghost !py-1 !px-2.5 text-xs" data-testid="preset-vip">VIP + Standard (46)</button>
          <button onClick={() => loadPreset('fosse')} className="btn-ghost !py-1 !px-2.5 text-xs" data-testid="preset-fosse">Fosse debout (100)</button>
        </div>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="zones-list">
          {zones.map((z, idx) => (
            <ZoneCard
              key={idx}
              zone={z}
              idx={idx}
              total={zones.length}
              onUpdate={(p) => updateZone(idx, p)}
              onRemove={() => removeZone(idx)}
              onMove={(dir) => moveZone(idx, dir)}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
          <button onClick={() => addZone(false)} className="btn-ghost !py-2 text-sm flex items-center gap-1" data-testid="add-zone-seated"><Plus className="w-4 h-4" /> Zone avec sièges</button>
          <button onClick={() => addZone(true)} className="btn-ghost !py-2 text-sm flex items-center gap-1" data-testid="add-zone-standing"><Plus className="w-4 h-4" /> Zone debout</button>
        </div>

        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={save} className="btn-primary flex items-center gap-1.5" data-testid="plan-save"><Save className="w-4 h-4" /> Enregistrer le plan</button>
        </div>
      </div>
    </div>
  );
}

function ZoneCard({ zone, idx, total, onUpdate, onRemove, onMove }) {
  const palette = COLOR_OPTIONS.find((c) => c.id === zone.color) || COLOR_OPTIONS[2];
  const tarifs = zone.tarifs || [];

  const setRows = (n) => {
    const letters = 'ABCDEFGHIJKLMNOPQR';
    const current = zone.rangees || [];
    if (n > current.length) {
      const cols = current[0]?.seats?.filter((s) => s !== '|').length || 8;
      const seats = [];
      const half = Math.floor(cols / 2);
      for (let i = 1; i <= cols; i++) { seats.push(i); if (i === half && cols >= 6) seats.push('|'); }
      const newRows = [...current];
      for (let i = current.length; i < n; i++) newRows.push({ row: letters[i] || `R${i + 1}`, seats: [...seats] });
      onUpdate({ rangees: newRows });
    } else {
      onUpdate({ rangees: current.slice(0, n) });
    }
  };

  const setSeatsPerRow = (n) => {
    const seats = [];
    const half = Math.floor(n / 2);
    for (let i = 1; i <= n; i++) { seats.push(i); if (i === half && n >= 6) seats.push('|'); }
    onUpdate({ rangees: (zone.rangees || []).map((r) => ({ ...r, seats: [...seats] })) });
  };

  const updateTarif = (i, patch) => onUpdate({ tarifs: tarifs.map((t, k) => k === i ? { ...t, ...patch } : (patch.default ? { ...t, default: false } : t)) });
  const removeTarif = (i) => onUpdate({ tarifs: tarifs.filter((_, k) => k !== i) });
  const addTarif = (preset) => {
    if (preset) {
      onUpdate({ tarifs: [...tarifs, { id: preset.id, label: preset.label, prix: preset.prix, quota: preset.quota || null, default: false, description: preset.description || '' }] });
    } else {
      onUpdate({ tarifs: [...tarifs, { id: `tarif-${tarifs.length + 1}`, label: 'Nouveau tarif', prix: 0, quota: null, default: false, description: '' }] });
    }
  };

  const seatsPerRow = zone.rangees?.[0]?.seats?.filter((s) => s !== '|').length || 0;

  return (
    <div className="border border-slate-200 rounded-lg p-3 sm:p-4 bg-white" data-testid={`zone-${idx}`} style={{ borderLeftWidth: 4, borderLeftColor: palette.hex }}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={zone.id}
            onChange={(e) => onUpdate({ id: e.target.value, label: e.target.value })}
            className="font-bold text-sm text-slate-900 bg-transparent border-0 border-b border-dashed border-slate-300 focus:outline-none focus:border-slate-500 px-0 w-full"
            placeholder="Nom de la zone"
            data-testid={`zone-${idx}-name`}
          />
        </div>
        <div className="flex gap-1">
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" aria-label="Monter"><ArrowUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" aria-label="Descendre"><ArrowDown className="w-3.5 h-3.5" /></button>
          <button onClick={onRemove} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" aria-label="Supprimer" data-testid={`zone-${idx}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        <div>
          <label className="label-uppercase block mb-1">Couleur</label>
          <select value={zone.color} onChange={(e) => onUpdate({ color: e.target.value })} className="input-base !py-1.5 !text-xs" data-testid={`zone-${idx}-color`}>
            {COLOR_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        {zone.standing ? (
          <div>
            <label className="label-uppercase block mb-1">Places debout</label>
            <input type="number" min="1" value={zone.places || ''} onChange={(e) => onUpdate({ places: e.target.value })} className="input-base !py-1.5 !text-xs" data-testid={`zone-${idx}-places`} />
          </div>
        ) : null}
      </div>

      {!zone.standing && (
        <div className="grid grid-cols-2 gap-2 text-xs mt-2">
          <div>
            <label className="label-uppercase block mb-1">Nombre de rangées</label>
            <input type="number" min="1" max="18" value={zone.rangees?.length || 0} onChange={(e) => setRows(+e.target.value)} className="input-base !py-1.5 !text-xs" data-testid={`zone-${idx}-rows`} />
          </div>
          <div>
            <label className="label-uppercase block mb-1">Sièges par rangée</label>
            <input type="number" min="1" max="20" value={seatsPerRow} onChange={(e) => setSeatsPerRow(+e.target.value)} className="input-base !py-1.5 !text-xs" data-testid={`zone-${idx}-seats-per-row`} />
          </div>
        </div>
      )}

      {/* Tarifs configurables (Phase D) */}
      <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <label className="label-uppercase flex items-center gap-1"><Tag className="w-3 h-3" /> Tarifs ({tarifs.length})</label>
          <div className="flex gap-1 flex-wrap">
            {TARIF_SUGGESTIONS.filter((s) => !tarifs.some((t) => t.id === s.id)).map((s) => (
              <button key={s.id} onClick={() => addTarif(s)} className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:border-slate-400 text-slate-600" data-testid={`zone-${idx}-add-tarif-${s.id}`}>+ {s.label}</button>
            ))}
            <button onClick={() => addTarif(null)} className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:border-slate-400 text-slate-600 flex items-center gap-1" data-testid={`zone-${idx}-add-tarif-custom`}><Plus className="w-2.5 h-2.5" /> Perso</button>
          </div>
        </div>
        <div className="space-y-2" data-testid={`zone-${idx}-tarifs-list`}>
          {tarifs.map((t, ti) => (
            <div key={ti} className="grid grid-cols-12 gap-1.5 items-center text-xs bg-slate-50 rounded p-2" data-testid={`zone-${idx}-tarif-${ti}`}>
              <input
                type="text"
                value={t.label}
                onChange={(e) => updateTarif(ti, { label: e.target.value })}
                className="input-base !py-1 !px-1.5 !text-xs col-span-4"
                placeholder="Nom du tarif"
                data-testid={`zone-${idx}-tarif-${ti}-label`}
              />
              <div className="col-span-3 flex items-center gap-1">
                <input
                  type="number" min="0" step="0.5" value={t.prix}
                  onChange={(e) => updateTarif(ti, { prix: e.target.value })}
                  className="input-base !py-1 !px-1.5 !text-xs flex-1"
                  data-testid={`zone-${idx}-tarif-${ti}-prix`}
                />
                <span className="text-slate-400">€</span>
              </div>
              <div className="col-span-3 flex items-center gap-1">
                <Users className="w-3 h-3 text-slate-400" />
                <input
                  type="number" min="0" placeholder="∞" value={t.quota ?? ''}
                  onChange={(e) => updateTarif(ti, { quota: e.target.value === '' ? null : +e.target.value })}
                  className="input-base !py-1 !px-1.5 !text-xs flex-1"
                  title="Quota max (vide = illimité)"
                  data-testid={`zone-${idx}-tarif-${ti}-quota`}
                />
              </div>
              <label className="col-span-1 flex items-center justify-center text-[10px] cursor-pointer" title="Tarif par défaut">
                <input type="radio" name={`def-${idx}`} checked={!!t.default} onChange={() => updateTarif(ti, { default: true })} className="cursor-pointer" data-testid={`zone-${idx}-tarif-${ti}-default`} />
              </label>
              <button onClick={() => removeTarif(ti)} className="col-span-1 text-rose-400 hover:text-rose-600 flex justify-center" aria-label="Supprimer" data-testid={`zone-${idx}-tarif-${ti}-remove`}><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {!tarifs.length && (
            <div className="text-[11px] text-rose-500 italic">Ajoutez au moins un tarif pour cette zone.</div>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="mt-3 p-2.5 bg-slate-50 rounded text-center text-[10px] text-slate-500" data-testid={`zone-${idx}-preview`}>
        {zone.standing ? (
          <>
            <span className="text-2xl">🕺💃</span>
            <div className="mt-1">{zone.places || 0} places debout · {palette.label}</div>
          </>
        ) : (
          <div className="inline-block">
            {(zone.rangees || []).map((r) => (
              <div key={r.row} className="flex items-center justify-center gap-0.5 my-0.5">
                <span className="w-3 text-[8px] text-slate-400">{r.row}</span>
                {r.seats.map((s, i) => s === '|'
                  ? <span key={i} className="w-1.5" />
                  : <span key={i} className="w-2 h-2 rounded-sm" style={{ background: palette.hex, opacity: 0.6 }} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
